import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { ArrowRightIcon, CheckCircle2Icon, BarChart3Icon, ShoppingCartIcon, SparklesIcon, ChevronDownIcon } from 'lucide-react';
export function FlowBoostPage() {
  usePageSEO({
    title: 'FlowBoost | Design Flow Digitals',
    description: 'FlowBoost by Design Flow Digitals — the ultimate Shopify speed optimization app. Boost Core Web Vitals, improve page load times, and increase conversions.'
  });
  return <div className="w-full" data-id="element-577">
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center pt-32 pb-20 overflow-hidden" data-id="element-578">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-black to-black pointer-events-none" data-id="element-579" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-580">
          <div data-id="element-581">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-blue-500/30 bg-blue-500/10" data-id="element-582">
              <SparklesIcon className="w-4 h-4 text-blue-400" data-id="element-583" />
              <span className="text-sm font-medium text-blue-300" data-id="element-584">
                Shopify App
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" data-id="element-585">
              FlowBoost
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 mb-10 leading-relaxed" data-id="element-586">
              Boost your Shopify store's conversion rate with intelligent upsell
              automation.
            </p>
            <div className="flex flex-wrap gap-4" data-id="element-587">
              <button className="bg-white text-black px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-200 transition-colors" data-id="element-588">
                Start Free Trial
              </button>
              <button className="px-8 py-4 rounded-full font-bold text-lg text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-589">
                View Demo
              </button>
            </div>
          </div>

          {/* Product Demo Mockup */}
          <div className="relative" data-id="element-590">
            <div className="absolute inset-0 bg-blue-500/20 blur-[100px] rounded-full" data-id="element-591" />
            <div className="glass-card border-white/10 p-2 rounded-2xl relative z-10 shadow-2xl shadow-black/50" data-id="element-592">
              <div className="bg-[#0A0A0A] rounded-xl overflow-hidden border border-white/5" data-id="element-593">
                {/* Fake App UI */}
                <div className="h-12 border-b border-white/10 flex items-center px-4 gap-2 bg-white/5" data-id="element-594">
                  <div className="w-3 h-3 rounded-full bg-red-500/50" data-id="element-595" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/50" data-id="element-596" />
                  <div className="w-3 h-3 rounded-full bg-green-500/50" data-id="element-597" />
                </div>
                <div className="p-8" data-id="element-598">
                  <div className="flex justify-between items-center mb-8" data-id="element-599">
                    <h3 className="text-xl font-bold text-white" data-id="element-600">
                      Upsell Performance
                    </h3>
                    <span className="text-green-400 bg-green-400/10 px-3 py-1 rounded-full text-sm" data-id="element-601">
                      +24.5%
                    </span>
                  </div>
                  <div className="h-40 bg-gradient-to-t from-blue-500/20 to-transparent border-b border-blue-500/50 relative mb-8" data-id="element-602">
                    {/* Fake chart line */}
                    <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-603">
                      <path d="M0,100 L20,80 L40,90 L60,40 L80,50 L100,20" fill="none" stroke="#3b82f6" strokeWidth="2" data-id="element-604" />
                    </svg>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-605">
                    <div className="bg-white/5 p-4 rounded-lg" data-id="element-606">
                      <p className="text-sm text-gray-400 mb-1" data-id="element-607">
                        Extra Revenue
                      </p>
                      <p className="text-2xl font-bold text-white" data-id="element-608">$12,450</p>
                    </div>
                    <div className="bg-white/5 p-4 rounded-lg" data-id="element-609">
                      <p className="text-sm text-gray-400 mb-1" data-id="element-610">
                        Conversion Rate
                      </p>
                      <p className="text-2xl font-bold text-white" data-id="element-611">4.8%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="section-padding bg-black" data-id="element-612">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-613">
          <h2 className="text-4xl font-bold mb-6" data-id="element-614">Key Features</h2>
          <p className="text-xl text-gray-400" data-id="element-615">
            Everything you need to maximize average order value.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-616">
          {[{
          icon: <SparklesIcon className="w-8 h-8 text-blue-400" data-id="element-617" />,
          title: 'AI Product Recommendations',
          desc: 'Machine learning algorithms suggest the perfect products based on cart contents.'
        }, {
          icon: <ShoppingCartIcon className="w-8 h-8 text-blue-400" data-id="element-618" />,
          title: 'Smart Cart Upsells',
          desc: 'In-cart offers that convert without interrupting the checkout flow.'
        }, {
          icon: <BarChart3Icon className="w-8 h-8 text-blue-400" data-id="element-619" />,
          title: 'Analytics Dashboard',
          desc: 'Track every extra dollar earned with detailed performance metrics.'
        }].map((feature, i) => <div key={i} className="glass-card p-8 border-white/10" data-id="element-620">
              <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-621">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-4" data-id="element-622">
                {feature.title}
              </h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-623">{feature.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Pricing */}
      <section className="section-padding" data-id="element-624">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-625">
          <h2 className="text-4xl font-bold mb-6" data-id="element-626">
            Simple, transparent pricing
          </h2>
          <p className="text-xl text-gray-400" data-id="element-627">
            Start free, upgrade when you grow.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-628">
          <div className="glass-card p-10 border-white/10" data-id="element-629">
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-630">Basic</h3>
            <p className="text-gray-400 mb-6" data-id="element-631">Perfect for new stores</p>
            <div className="mb-8" data-id="element-632">
              <span className="text-5xl font-bold text-white" data-id="element-633">$19</span>
              <span className="text-gray-400" data-id="element-634">/mo</span>
            </div>
            <ul className="space-y-4 mb-8" data-id="element-635">
              {['Up to 500 orders/mo', 'Basic recommendations', 'Standard support'].map((f, i) => <li key={i} className="flex items-center gap-3 text-gray-300" data-id="element-636">
                  <CheckCircle2Icon className="w-5 h-5 text-blue-400" data-id="element-637" /> {f}
                </li>)}
            </ul>
            <button className="w-full py-4 rounded-lg font-bold text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-638">
              Start 14-Day Trial
            </button>
          </div>

          <div className="glass-card p-10 border-blue-500/30 bg-blue-500/5 relative overflow-hidden" data-id="element-639">
            <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-640">
              POPULAR
            </div>
            <h3 className="text-2xl font-bold text-white mb-2" data-id="element-641">Pro</h3>
            <p className="text-gray-400 mb-6" data-id="element-642">For growing businesses</p>
            <div className="mb-8" data-id="element-643">
              <span className="text-5xl font-bold text-white" data-id="element-644">$49</span>
              <span className="text-gray-400" data-id="element-645">/mo</span>
            </div>
            <ul className="space-y-4 mb-8" data-id="element-646">
              {['Unlimited orders', 'AI-powered recommendations', 'A/B testing', 'Priority support'].map((f, i) => <li key={i} className="flex items-center gap-3 text-gray-300" data-id="element-647">
                  <CheckCircle2Icon className="w-5 h-5 text-blue-400" data-id="element-648" /> {f}
                </li>)}
            </ul>
            <button className="w-full py-4 rounded-lg font-bold bg-white text-black hover:bg-gray-200 transition-colors" data-id="element-649">
              Start 14-Day Trial
            </button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6 text-center bg-gradient-to-b from-transparent to-blue-900/20" data-id="element-650">
        <h2 className="text-4xl md:text-5xl font-bold mb-8" data-id="element-651">
          Ready to boost your revenue?
        </h2>
        <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-652">
          Join thousands of merchants using FlowBoost to increase their average
          order value.
        </p>
        <button className="bg-white text-black px-10 py-5 rounded-full font-bold text-lg hover:scale-105 transition-transform" data-id="element-653">
          Install FlowBoost Now
        </button>
      </section>
    </div>;
}